import { useState, useEffect, useMemo, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { MultiSelect } from '@/components/MultiSelect';

export interface AddStandardItemModalProps {
  open: boolean;
  availableItems: any[];
  loading?: boolean;        // show spinner while items are being fetched
  itemLabelKey: string;     // e.g. 'value', 'dispositionValue'
  itemValueKey: string;     // e.g. 'code', 'dispositionCode'
  title: string;            // e.g. 'Add Standard Nature of Call'
  fieldLabel: string;       // e.g. 'Nature of Call(s) to add'
  placeholder?: string;     // e.g. 'Select nature of call...'
  requiredMessage?: string; // e.g. 'Nature of call(s) required.'
  theme?: string;
  onSave: (selectedCodes: number[]) => void;
  onCancel: () => void;
}

export function AddStandardItemModal({
  open,
  availableItems = [],
  loading = false,
  itemLabelKey,
  itemValueKey,
  title,
  fieldLabel,
  placeholder = 'Select items...',
  requiredMessage = 'Selection required.',
  theme,
  onSave,
  onCancel,
}: AddStandardItemModalProps) {
  const [selectedCodes, setSelectedCodes] = useState<number[]>([]);

  // Reset selection when opening
  useEffect(() => {
    if (open) {
      setSelectedCodes([]);
    }
  }, [open]);

  const items = useMemo(
    () =>
      availableItems.map((item) => ({
        label: item[itemLabelKey],
        value: item[itemValueKey],
      })),
    [availableItems, itemLabelKey, itemValueKey]
  );

  const handleSave = useCallback(() => {
    if (!selectedCodes || selectedCodes.length === 0) {
      (window as any).App?.showPopupNotification?.(requiredMessage, 'error');
      return;
    }

    onSave(selectedCodes.slice());
  }, [selectedCodes, requiredMessage, onSave]);

  return (
    <BaseFormModal
      open={open}
      title={title}
      theme={theme}
      dialogClass="kstc-dialog--form"
      saveText="Add"
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">{fieldLabel}</label>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <MultiSelect
            value={selectedCodes}
            items={items}
            labelKey="label"
            valueKey="value"
            placeholder={placeholder}
            sortItems={true}
            onChange={setSelectedCodes}
          />
        )}
      </div>
    </BaseFormModal>
  );
}
